// src/pages/EditCustomer.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  Heading,
  VStack,
  HStack,
  useToast,
  Grid,
  FormHelperText,
  Flex,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../firebaseConfig';
import useOrgSettings from '../hooks/useOrgSettings';

// Time slots: 10:00 → 22:00 (inclusive), 30-minute intervals
const TIME_SLOTS = (() => {
  const slots = [];
  for (let h = 10; h <= 22; h++) {
    for (let m of [0, 30]) {
      if (h === 22 && m === 30) continue; // include 22:00 but not 22:30
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      slots.push(`${hh}:${mm}`);
    }
  }
  return slots;
})();

export default function EditCustomer() {
  const toast = useToast();
  const navigate = useNavigate();
  const { id: leadId } = useParams();
  const { orgId } = useOrgSettings();

  const functions = useMemo(() => getFunctions(undefined, 'europe-west2'), []);
  const fnUpdate = useMemo(() => httpsCallable(functions, 'adminUpdateLead'), [functions]);

  const [loading, setLoading] = useState(true);
  const [lead, setLead] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    customerTitle: 'Mr & Mrs',
    customerFirstName: '',
    customerLastName: '',
    customerPhone: '',
    customerEmail: '',
    addressLine1: '',
    addressLine2: '',
    postcode: '',
    apptDate: '',
    apptTime: '',
    source: '',
    adminNotes: '',
    sellerBrief: '',
  });

  // Helpers to split and join appointment
  const splitAppointment = (appointment) => {
    if (!appointment) return { apptDate: '', apptTime: '' };
    try {
      const d = new Date(appointment);
      if (isNaN(d.getTime())) return { apptDate: '', apptTime: '' };
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const hh = String(d.getHours()).padStart(2, '0');
      const mi = String(d.getMinutes()).padStart(2, '0');
      return { apptDate: `${yyyy}-${mm}-${dd}`, apptTime: `${hh}:${mi}` };
    } catch {
      return { apptDate: '', apptTime: '' };
    }
  };

  // --- Minimal change starts here ---
  const isFinishedFlag = (v) =>
    String(v ?? '')
      .trim()
      .toUpperCase() === 'FINISHED';
  // --- Minimal change ends here ---

  useEffect(() => {
    if (!orgId || !leadId) return;
    (async () => {
      try {
        setLoading(true);
        const ref = doc(db, `organizations/${orgId}/leads/${leadId}`);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          toast({ title: 'Lead not found', status: 'error' });
          navigate('/leads');
          return;
        }
        const data = { id: snap.id, ...snap.data() };

        // --- Minimal change: allow edit if status is FINISHED OR apptEndAt exists ---
        const finished = isFinishedFlag(data.status) || Boolean(data.apptEndAt);

        // If assigned and NOT finished, block editing here — send user back with message
        if (data.salespersonId && !finished) {
          toast({
            title: 'Unassign before editing',
            description: `This lead is assigned to ${data.salespersonName || 'a sales rep'}. Unassign it first to edit customer details.`,
            status: 'info',
            isClosable: true,
          });
          navigate('/leads');
          return;
        }
        // --- End of minimal change ---

        const { apptDate, apptTime } = splitAppointment(data.appointment);

        setLead(data);
        setForm({
          customerTitle: data.customerTitle || 'Mr & Mrs',
          customerFirstName: data.customerFirstName || '',
          customerLastName: data.customerLastName || '',
          customerPhone: data.customerPhone || '',
          customerEmail: data.customerEmail || '',
          addressLine1: data.addressLine1 || '',
          addressLine2: data.addressLine2 || '',
          postcode: data.postcode || '',
          apptDate,
          apptTime,
          source: data.source || '',
          adminNotes: data.adminNotes || '',
          sellerBrief: data.sellerBrief || data.salesNotes || '',
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [orgId, leadId, navigate, toast]);

  const onChange = (e) => setForm({ ...form, [form && e.target.name]: e.target.value });

  // Same required fields policy as NewLead (mirror): sellerBrief IS required; first name optional
  const canSave = React.useMemo(() => {
    const req = [
      'customerLastName',
      'customerPhone',
      'customerEmail',
      'addressLine1',
      'postcode',
      'apptDate',
      'apptTime',
      'sellerBrief',
    ];
    return req.every((k) => String(form[k] || '').trim());
  }, [form]);

  const handleCancel = () => {
    const ok = window.confirm('Discard changes and go back to Leads?');
    if (!ok) return;
    navigate('/leads');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!orgId || !lead) return;
    if (!canSave) {
      toast({ title: 'Please fill all required fields', status: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const appointment = `${form.apptDate}T${form.apptTime}`;
      const patch = {
        customerTitle: (form.customerTitle || '').trim(),
        customerFirstName: (form.customerFirstName || '').trim(),
        customerLastName: form.customerLastName.trim(),
        customerPhone: form.customerPhone.trim(),
        customerEmail: form.customerEmail.trim(),
        addressLine1: form.addressLine1.trim(),
        addressLine2: (form.addressLine2 || '').trim(),
        postcode: form.postcode.trim(),
        appointment,
        source: (form.source || '').trim(),
        adminNotes: (form.adminNotes || '').trim(),
        sellerBrief: form.sellerBrief.trim(),
        salesNotes: form.sellerBrief.trim(), // legacy/back-compat
      };

      await fnUpdate({ orgId, leadId: lead.id, patch });
      toast({ title: 'Customer details updated', status: 'success' });
      navigate('/leads');
    } catch (e) {
      console.error('[EditCustomer] update error:', e);
      toast({ title: 'Update failed', description: e.message, status: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box p={6}>
        <HStack spacing={4}>
          <Spinner size="lg" />
          <Text fontSize="lg">Loading…</Text>
        </HStack>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <Box borderWidth={1} borderRadius="lg" p={6} mt={6} boxShadow="lg" bg="white">
        <Heading size="lg" mb={6} color="blue.600">
          Edit Customer
        </Heading>
        <Text fontSize="md" color="gray.600" mb={6}>
          Update the customer and appointment details for this lead.
        </Text>

        <form onSubmit={handleSubmit}>
          <VStack align="stretch" spacing={6}>
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                  Title
                </FormLabel>
                <Select
                  size="lg"
                  name="customerTitle"
                  value={form.customerTitle}
                  onChange={onChange}
                >
                  <option>Mr &amp; Mrs</option>
                  <option>Mr</option>
                  <option>Mrs</option>
                  <option>Miss</option>
                  <option>Ms</option>
                  <option>Dr</option>
                  <option>Prof</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                  First name (optional)
                </FormLabel>
                <Input
                  size="lg"
                  name="customerFirstName"
                  value={form.customerFirstName}
                  onChange={onChange}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                  Last name
                </FormLabel>
                <Input
                  size="lg"
                  name="customerLastName"
                  value={form.customerLastName}
                  onChange={onChange}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                  Phone
                </FormLabel>
                <Input
                  size="lg"
                  name="customerPhone"
                  value={form.customerPhone}
                  onChange={onChange}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                  Email
                </FormLabel>
                <Input
                  size="lg"
                  type="email"
                  name="customerEmail"
                  value={form.customerEmail}
                  onChange={onChange}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                  Address line 1
                </FormLabel>
                <Input
                  size="lg"
                  name="addressLine1"
                  value={form.addressLine1}
                  onChange={onChange}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                  Address line 2
                </FormLabel>
                <Input
                  size="lg"
                  name="addressLine2"
                  value={form.addressLine2}
                  onChange={onChange}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                  Postcode
                </FormLabel>
                <Input size="lg" name="postcode" value={form.postcode} onChange={onChange} />
              </FormControl>

              {/* Date + Time pickers (mirror NewLead) */}
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                  Date
                </FormLabel>
                <Input
                  size="lg"
                  type="date"
                  name="apptDate"
                  value={form.apptDate}
                  onChange={onChange}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                  Time
                </FormLabel>
                <Select size="lg" name="apptTime" value={form.apptTime} onChange={onChange}>
                  {/* Ensure current time appears even if not in TIME_SLOTS */}
                  {form.apptTime && !TIME_SLOTS.includes(form.apptTime) && (
                    <option value={form.apptTime}>{form.apptTime} (current)</option>
                  )}
                  <option value="">Select time…</option>
                  {TIME_SLOTS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                  Internal Notes
                </FormLabel>
                <Textarea size="lg" name="adminNotes" value={form.adminNotes} onChange={onChange} />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                  Brief to Sales Rep
                </FormLabel>
                <Textarea
                  size="lg"
                  name="sellerBrief"
                  value={form.sellerBrief}
                  onChange={onChange}
                />
                <FormHelperText>
                  Include measure prompt (e.g. “3 w 1d 1fd”), access/parking info, pets, language
                  preferences, any constraints, and anything else to help the rep.
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                  Source (optional)
                </FormLabel>
                <Input size="lg" name="source" value={form.source} onChange={onChange} />
              </FormControl>
            </Grid>
          </VStack>

          <Flex justify="flex-end" pt={4}>
            <HStack spacing={3}>
              <Button size="lg" type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                size="lg"
                type="submit"
                colorScheme="blue"
                isLoading={saving}
                isDisabled={!canSave}
              >
                Save Changes
              </Button>
            </HStack>
          </Flex>
        </form>
      </Box>
    </Box>
  );
}
